import React, { useState } from 'react';
import { Table, Tooltip, Modal, Typography } from 'antd';
import { DoneRounded, BackspaceRounded } from "@mui/icons-material";
import { shortenSector } from "../../../utils/abbreviations";
import ProportionLine from "../../../charts/ProportionLine";


const SectorKeptRemovedTable = ({ data }) => {
    const { items, weightKept, weightRemoved } = data;

    const dataSource = [
        ...items.slice(0, -1).map(x => ({
            ticker: x.name,
            wgt: x.value,
            kept: true
        })),
        ...items[items.length - 1].children.map(x => ({
            ticker: x.name,
            wgt: x.value,
            kept: false
        }))
    ];

    const columns = [
        {
            title: 'Ticker',
            dataIndex: 'ticker',
            key: 'ticker',
            render: (text, record) => (
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    background: record.kept ? 'rgba(39, 216, 135, 0.1)' : 'rgba(255, 0, 0, 0.1)',
                    padding: '4px 8px',
                    borderRadius: '4px'
                }}>
                    <Typography.Text strong>{text.toUpperCase()}</Typography.Text>
                </div>
            )
        },
        {
            title: 'Weight',
            dataIndex: 'wgt',
            key: 'wgt',
            render: (text, record) => (
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    background: record.kept ? 'rgba(39, 216, 135, 0.1)' : 'rgba(255, 0, 0, 0.1)',
                    padding: '4px 8px',
                    borderRadius: '4px'
                }}>
                    <Typography.Text>{(text * 100).toFixed(2)}%</Typography.Text>
                </div>
            )
        }
    ];

    return (
        <>
            <Table
                dataSource={dataSource}
                columns={columns}
                pagination={false}
                size={'small'}
                rowClassName={(record) => (record.kept ? 'kept-row' : 'removed-row')}
                scroll={{ y: 400 }}
            />
        </>
    );
};



const SectorLegend = ({ data }) => {
    const [showModal, setShowModal] = useState(false);
    const [modalData, setModalData] = useState(null);

    const handleSectorClick = (data) => {
        setModalData(data);
        setShowModal(true);
    };

    const sectorData = data.children.filter(x => x.name.length > 0).map((x, index) => {
        const items = x.children;
        let weightKept = [...items.slice(0, -1)].reduce((acc, item) => acc + item.value, 0);
        let weightRemoved = items[items.length - 1].children.reduce((acc, item) => acc + item.value, 0);

        // Normalize weights to sum to 1
        const totalWeight = weightKept + weightRemoved;
        const normWeightKept = totalWeight > 0 ? weightKept / totalWeight : 0;
        const normWeightRemoved = 1 - normWeightKept;

        const kept = items.length > 1 ? items.length - 1 : 0;
        const removed = items[items.length - 1].children.length;

        // Dynamic sector summary
        const summary = `For this sector, we kept ${kept} stocks totaling ${(normWeightKept * 100).toFixed(2)}% of the normalized weight, and removed ${removed} stocks amounting to ${(normWeightRemoved * 100).toFixed(2)}% of the normalized weight.`;

        return {
            key: index,
            sector: x.name,
            kept,
            removed,
            total: kept + removed,
            weightKept,
            weightRemoved,
            normWeightKept,
            normWeightRemoved,
            items,
            summary
        };
    });

    const sortedSectorData = sectorData.sort((a, b) => b.kept - a.kept);

    const columns = [
        {
            title: 'Sector',
            dataIndex: 'sector',
            key: 'sector',
            render: (sector, record) => <Tooltip title={record.summary}><a onClick={() => handleSectorClick(record)} style={{ textTransform: 'uppercase', fontWeight: '400' }}>{shortenSector(sector)}</a></Tooltip>,
            width: '250px',
        },
        {
            title: 'Weight',
            key: 'values',
            render: (_, record) => (
                <Tooltip title={`Kept: ${(record.normWeightKept * 100).toFixed(2)}%, Removed: ${(record.normWeightRemoved * 100).toFixed(2)}%`}>
                    <ProportionLine keptPercentage={record.normWeightKept} removedPercentage={record.normWeightRemoved} />
                </Tooltip>
            ),
        },
        {
            title: 'Count',
            key: 'values',
            render: (_, record) => (
                <Tooltip title={`Kept: ${((record.kept/record.total) * 100).toFixed(2)}%, Removed: ${((record.removed/record.total) * 100).toFixed(2)}%`}>
                    <ProportionLine keptPercentage={record.kept/record.total} removedPercentage={record.removed/record.total} />
                </Tooltip>
            ),
        },
    ];

    return (
        <>
            <Table
                dataSource={sortedSectorData}
                columns={columns}
                pagination={false}
                rowClassName="sector-row"
            />

            <Modal
                visible={showModal}
                onCancel={() => setShowModal(false)}
                footer={null}
                title={`Details for ${modalData ? modalData.sector : ''}`}
            >
                {modalData && <SectorKeptRemovedTable data={modalData} />}
            </Modal>
        </>
    );
};

export default SectorLegend;
